import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Alert,
  Badge,
  Button,
  FormGroup,
  Input,
  Label,
  Spinner,
} from "reactstrap";

import { canManageClinicalRecords } from "../../helpers/clinicalRecordAccess";
import {
  acceptImageMarketingConsent,
  getImageMarketingConsent,
  ImageMarketingConsentState,
  revokeImageMarketingConsent,
} from "../../services/clientImageMarketingConsentService";
import ConsentTermPreviewModal from "./ConsentTermPreviewModal";

type Props = {
  clientId: number;
  /** Deep link from nurture / in-app notification (`?focus=image_marketing`). */
  highlight?: boolean;
  onChanged?: (state: ImageMarketingConsentState) => void;
};

const ClientImageMarketingConsentCard = ({
  clientId,
  highlight = false,
  onChanged,
}: Props) => {
  const { t } = useTranslation();
  const canManage = canManageClinicalRecords();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState<ImageMarketingConsentState | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [termReadConfirmed, setTermReadConfirmed] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [renewSoonOpen, setRenewSoonOpen] = useState(false);
  const [focusRing, setFocusRing] = useState(highlight);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getImageMarketingConsent(clientId);
      setState(response.data);
      onChanged?.(response.data);
    } catch (error: unknown) {
      const apiError = error as { data?: string };
      toast.error(apiError.data || t("clients.imageMarketing.loadError"));
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, onChanged, t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!highlight || loading || !state) {
      return;
    }

    setFocusRing(true);
    if (state.expires_soon && state.status === "active" && !state.consent_term_outdated) {
      setRenewSoonOpen(true);
    }

    const clearRing = window.setTimeout(() => setFocusRing(false), 6000);
    return () => window.clearTimeout(clearRing);
  }, [highlight, loading, state]);

  const statusColor = (status: string): string => {
    if (status === "active") {
      return "success";
    }
    if (status === "expired") {
      return "warning";
    }
    if (status === "revoked") {
      return "secondary";
    }
    return "light";
  };

  const handleAccept = async () => {
    if (!canManage || !accepted || !termReadConfirmed) {
      return;
    }

    setSaving(true);
    try {
      const response = await acceptImageMarketingConsent(clientId, {
        accepted: true,
        consent_term_id: state?.active_term?.id ?? null,
        consent_text_snapshot: state?.active_term?.body_text ?? null,
      });
      toast.success(
        response.message ||
          (state?.consent_term_outdated
            ? t("clients.imageMarketing.renewSuccess")
            : t("clients.imageMarketing.acceptSuccess"))
      );
      setAccepted(false);
      setTermReadConfirmed(false);
      setRenewSoonOpen(false);
      setState(response.data);
      onChanged?.(response.data);
    } catch (error: unknown) {
      const apiError = error as { data?: string };
      toast.error(apiError.data || t("clients.imageMarketing.acceptError"));
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async () => {
    if (!canManage) {
      return;
    }

    setSaving(true);
    try {
      const response = await revokeImageMarketingConsent(clientId);
      toast.success(response.message || t("clients.imageMarketing.revokeSuccess"));
      setState(response.data);
      onChanged?.(response.data);
    } catch (error: unknown) {
      const apiError = error as { data?: string };
      toast.error(apiError.data || t("clients.imageMarketing.revokeError"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2 text-muted small py-2">
        <Spinner size="sm" />
        {t("clients.imageMarketing.loading")}
      </div>
    );
  }

  const status = state?.status ?? "absent";
  const hasTerm = Boolean(state?.active_term);
  const isActive = status === "active";
  const isOutdated = Boolean(state?.consent_term_outdated);
  const expiresSoon = Boolean(state?.expires_soon);
  const showAcceptForm =
    hasTerm && (!isActive || isOutdated || (expiresSoon && renewSoonOpen));

  return (
    <div
      className={`mb-3 rounded ${focusRing ? "border border-warning border-2 p-2 bg-warning-subtle" : ""}`}
      data-clarity-mask="true"
      id="client-image-marketing-consent"
    >
      {focusRing ? (
        <p className="small text-warning-emphasis mb-2 mb-md-2">
          {t("clients.imageMarketing.focusFromAlert")}
        </p>
      ) : null}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <h6 className="fs-13 mb-0">{t("clients.imageMarketing.title")}</h6>
        <Badge color={statusColor(status)} className="text-uppercase">
          {t(`clients.imageMarketing.status.${status}`)}
        </Badge>
        {isOutdated ? (
          <Badge color="warning" className="text-uppercase">
            {t("clients.imageMarketing.status.outdated")}
          </Badge>
        ) : null}
      </div>
      <p className="text-muted small mb-2">{t("clients.imageMarketing.hint")}</p>

      {!hasTerm ? (
        <Alert color="warning" className="border-0 mb-0 py-2">
          <p className="mb-2 small">{t("clients.imageMarketing.noTerm")}</p>
          <Button
            tag={Link}
            to="/settings/anamnesis-templates"
            color="soft-warning"
            size="sm"
          >
            {t("clients.imageMarketing.configureTermCta")}
          </Button>
        </Alert>
      ) : null}

      {showAcceptForm ? (
        <div className="border rounded p-3 bg-light">
          {isOutdated ? (
            <Alert color="warning" className="border-0 py-2 mb-2">
              <p className="mb-0 small">{t("clients.imageMarketing.outdatedHint")}</p>
            </Alert>
          ) : null}
          {expiresSoon && renewSoonOpen && !isOutdated ? (
            <Alert color="warning" className="border-0 py-2 mb-2">
              <p className="mb-0 small">
                {t("clients.imageMarketing.expiresSoonHint", {
                  days: state?.expires_in_days ?? 0,
                })}
              </p>
            </Alert>
          ) : null}
          <p className="small mb-2">
            <Button
              color="link"
              className="p-0 align-baseline"
              type="button"
              onClick={() => setPreviewOpen(true)}
            >
              {t("clients.imageMarketing.readTerm")}
            </Button>
            {termReadConfirmed ? (
              <Badge color="success" className="ms-2">
                {t("clients.imageMarketing.termReadOk")}
              </Badge>
            ) : null}
          </p>
          <FormGroup check className="mb-3">
            <Input
              id={`img-mkt-accept-${clientId}`}
              type="checkbox"
              checked={accepted}
              disabled={!canManage || saving || !termReadConfirmed}
              onChange={(event) => setAccepted(event.target.checked)}
            />
            <Label check htmlFor={`img-mkt-accept-${clientId}`} className="small">
              {t("clients.imageMarketing.checkboxLabel")}
            </Label>
          </FormGroup>
          {!termReadConfirmed ? (
            <p className="text-muted small mb-2">
              {t("clients.imageMarketing.readTermFirst")}
            </p>
          ) : null}
          {canManage ? (
            <Button
              color="primary"
              size="sm"
              type="button"
              disabled={!accepted || !termReadConfirmed || saving}
              onClick={() => void handleAccept()}
            >
              {saving ? <Spinner size="sm" className="me-1" /> : null}
              {isOutdated
                ? t("clients.imageMarketing.renewCta")
                : t("clients.imageMarketing.acceptCta")}
            </Button>
          ) : (
            <p className="text-muted small mb-0">{t("clients.imageMarketing.readOnly")}</p>
          )}
        </div>
      ) : null}

      {hasTerm && isActive && !isOutdated && !(expiresSoon && renewSoonOpen) ? (
        <Alert
          color={expiresSoon ? "warning" : "success"}
          className="border-0 mb-0 py-2"
        >
          <p className="mb-2 small">
            {expiresSoon
              ? t("clients.imageMarketing.expiresSoonHint", {
                  days: state?.expires_in_days ?? 0,
                })
              : t("clients.imageMarketing.activeHint")}
          </p>
          {canManage ? (
            <div className="d-flex flex-wrap gap-2">
              {expiresSoon ? (
                <Button
                  color="primary"
                  size="sm"
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    setAccepted(false);
                    setTermReadConfirmed(false);
                    setRenewSoonOpen(true);
                  }}
                >
                  {t("clients.imageMarketing.renewCta")}
                </Button>
              ) : null}
              <Button
                color="soft-danger"
                size="sm"
                type="button"
                disabled={saving}
                onClick={() => void handleRevoke()}
              >
                {saving ? <Spinner size="sm" className="me-1" /> : null}
                {t("clients.imageMarketing.revokeCta")}
              </Button>
            </div>
          ) : null}
        </Alert>
      ) : null}

      {state?.active_term ? (
        <ConsentTermPreviewModal
          isOpen={previewOpen}
          title={state.active_term.title}
          body={state.active_term.body_text}
          onClose={() => setPreviewOpen(false)}
          requireScrollToEnd
          onConfirmedRead={() => {
            setTermReadConfirmed(true);
            setPreviewOpen(false);
          }}
        />
      ) : null}
    </div>
  );
};

export default ClientImageMarketingConsentCard;
